'use client';

import { useState, useCallback } from 'react';
import { useToast } from '@/components/shared/ui/toast';
import { batchInvalidate, type InvalidationType } from './mutations';

/**
 * Configuration for modal mutation hooks.
 * Extends optimistic mutation pattern with modal-specific behavior.
 */
export interface UseModalMutationOptions<TVariables, TData> {
  /**
   * The mutation function (API call).
   * Receives the form data and returns a promise.
   */
  mutationFn: (variables: TVariables) => Promise<TData>;

  /**
   * Business ID for cache invalidation.
   */
  businessId: string;

  /**
   * Cache invalidation types to trigger after success.
   */
  invalidate: InvalidationType[];

  /**
   * Success message to show in toast.
   * Can be a string or function that receives the result data.
   */
  successMessage?: string | ((data: TData, variables: TVariables) => string);

  /**
   * Error message to show in toast.
   * Can be a string or function that receives the error.
   */
  errorMessage?: string | ((error: Error) => string);

  /**
   * Callback after successful mutation (before modal closes).
   */
  onSuccess?: (data: TData) => void;

  /**
   * Callback on error.
   */
  onError?: (error: Error) => void;

  /**
   * Delay before showing success toast and closing modal (ms).
   * Default: 1500ms (1.5s) for smooth UX.
   */
  feedbackDelay?: number;

  /**
   * Optional optimistic update function.
   * Called immediately before API call for instant UI feedback.
   */
  optimisticUpdate?: (variables: TVariables) => void;
}

export interface UseModalMutationReturn<TVariables> {
  /**
   * Execute the mutation.
   * Returns a promise that resolves when modal should close.
   */
  mutate: (variables: TVariables) => Promise<void>;

  /**
   * Whether the mutation is currently in progress.
   */
  isLoading: boolean;

  /**
   * The last error that occurred.
   */
  error: Error | null;
}

/**
 * Hook for modal form submissions with optimistic UI.
 *
 * Flow:
 * 1. User submits form
 * 2. Apply optimistic update (if provided)
 * 3. Wait 1-2s for smooth UX
 * 4. Show success toast
 * 5. Close modal automatically
 * 6. Fire API call in background
 * 7. On success: Invalidate caches
 * 8. On error: Show error, keep modal open
 *
 * @example
 * ```tsx
 * const { mutate, isLoading } = useModalMutation({
 *   mutationFn: createCustomer,
 *   businessId,
 *   invalidate: ['customers'],
 *   successMessage: (customer) => `${customer.name} added`,
 *   optimisticUpdate: (data) => {
 *     optimisticAddCustomer(businessId, { ...data, id: 'temp-id' });
 *   },
 * });
 *
 * const handleSubmit = async (data) => {
 *   await mutate(data); // Modal closes automatically on success
 * };
 * ```
 */
export function useModalMutation<TVariables, TData>(
  options: UseModalMutationOptions<TVariables, TData>
): UseModalMutationReturn<TVariables> {
  const {
    mutationFn,
    businessId,
    invalidate,
    successMessage,
    errorMessage = 'Operation failed. Please try again.',
    onSuccess,
    onError,
    feedbackDelay = 1500,
    optimisticUpdate,
  } = options;

  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (variables: TVariables): Promise<void> => {
      // Prevent concurrent mutations
      if (isLoading) return;

      setIsLoading(true);
      setError(null);

      try {
        // Apply optimistic update immediately (if provided)
        optimisticUpdate?.(variables);

        // Start API call in background (fire-and-forget)
        const mutationPromise = mutationFn(variables);

        // Wait for feedback delay before showing success
        await new Promise((resolve) => setTimeout(resolve, feedbackDelay));

        // Show success toast
        const message =
          typeof successMessage === 'function'
            ? successMessage(await mutationPromise, variables)
            : successMessage || 'Success';
        addToast({ message, type: 'success' });

        // Wait for mutation to complete
        const data = await mutationPromise;

        // Call success callback
        onSuccess?.(data);

        // Invalidate caches in background
        setTimeout(() => {
          batchInvalidate(businessId, invalidate);
        }, 100);

        // Modal closes automatically (caller handles this via resolved promise)
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);

        // Show error toast
        const message =
          typeof errorMessage === 'function'
            ? errorMessage(error)
            : errorMessage;
        addToast({ message, type: 'error' });

        // Call error callback
        onError?.(error);

        // Re-throw to allow caller to handle (modal stays open)
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [
      isLoading,
      mutationFn,
      businessId,
      invalidate,
      successMessage,
      errorMessage,
      onSuccess,
      onError,
      feedbackDelay,
      optimisticUpdate,
      addToast,
    ]
  );

  return {
    mutate,
    isLoading,
    error,
  };
}
